import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { toast } from 'sonner';
import { TrendUp, Eye, EyeSlash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { extractApiError } from '@/lib/apiErrors';
import Logo from '@/components/Logo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success(t('login.success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(extractApiError(error, t('login.error'), language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <LanguageSwitcher />
          </div>

          <h1 className="heading-2 text-[#09090B] mb-2" data-testid="login-title">
            {t('login.title')}
          </h1>
          <p className="text-[#52525B] mb-8">{t('login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#09090B]">{t('login.email')}</Label>
              <Input
                id="email" type="email" placeholder={t('login.email.placeholder')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36]"
                required data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#09090B]">{t('login.password')}</Label>
              <div className="relative">
                <Input
                  id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 rounded-none border-[#E4E4E7] focus:ring-[#0F1B36] pr-12"
                  required data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#09090B]"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-12 rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#52525B]">
            {t('login.no_account')}{' '}
            <Link to="/register" className="text-[#0F1B36] hover:text-[#00227A] font-medium" data-testid="login-register-link">
              {t('login.create_one')}
            </Link>
          </p>
          <p className="mt-3 text-center">
            <Link to="/reset-password" className="text-[#52525B] hover:text-[#0F1B36] text-sm" data-testid="forgot-password-link">
              {t('login.forgot_password')}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[#09090B] items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="text-6xl font-black text-white mb-6">87%</div>
          <p className="text-xl text-[#A1A1AA] mb-4">{t('login.stat')}</p>
          <div className="flex items-center justify-center gap-4">
            <img 
              src="https://images.unsplash.com/photo-1629507313712-f21468afdf2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTgxMzU5OHww&ixlib=rb-4.1.0&q=85"
              alt="Testimonial" className="w-12 h-12 object-cover"
            />
            <div className="text-left">
              <p className="text-white font-medium">Sarah Chen</p>
              <p className="text-[#52525B] text-sm">VP of Digital, TechCorp</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
